/**
 * NILE Country/Language Redirect System
 * 
 * This script handles:
 * 1. Cross-domain cookie management for country/language preferences
 * 2. Automatic redirect to correct store based on saved preferences
 * 3. Country detection for first-time visitors
 * 4. Language selection based on browser/country defaults
 */

(function () {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  const COOKIE_NAME = 'nile_prefs';
  const COOKIE_EXPIRY_DAYS = 730; // 2 years

  // Store configuration - maps store IDs to their countries and URLs.
  // primaryLanguage is the language served at each store's root (GBP: English).
  const STORE_CONFIG = {
    chf: {
      countries: ['CH', 'LI', 'CA', 'NO'],
      primaryLanguage: 'de',
      productionDomains: ['nile.ch', 'www.nile.ch'],
      testDomains: ['nile-ch.myshopify.com', 'nile-online-chf.myshopify.com']
    },
    eur: {
      // 2026-08-12 (INC-1461): EU packaging regulations - every EU market except
      // Germany and Austria was deactivated in Shopify EUR. Kept commented so the
      // full list can be restored by swapping the two lines back.
      // countries: ['AT', 'BE', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
      countries: ['AT', 'DE'],
      primaryLanguage: 'de',
      productionDomains: ['eu.nile.ch'],
      testDomains: ['nile-online-eur.myshopify.com', 'nile-online-eur-test.myshopify.com']
    },
    gbp: {
      countries: ['GB'],
      primaryLanguage: 'en',
      productionDomains: ['uk.nile.ch'],
      testDomains: ['nile-online-gbp-prod.myshopify.com', 'nile-online-test-gbp.myshopify.com']
    }
  };

  const DEFAULT_PRIMARY_LANGUAGE = 'de';

  // Country to default language mapping
  const COUNTRY_DEFAULT_LANGUAGE = {
    // German-speaking countries
    CH: 'de', DE: 'de', AT: 'de', LI: 'de',
    // French-speaking countries
    FR: 'fr', BE: 'fr', LU: 'fr',
    // Italian-speaking
    IT: 'it',
    // English-speaking
    GB: 'en', IE: 'en', CA: 'en',
    // Default for all others
    default: 'de'
  };

  const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'];

  // ============================================
  // COOKIE MANAGEMENT
  // ============================================

  /**
   * Get the cookie domain for cross-subdomain sharing
   * Returns .nile.ch for production, or current domain for test environments
   */
  function getCookieDomain() {
    const hostname = window.location.hostname;

    // For nile.ch and subdomains (eu.nile.ch, gb.nile.ch), use .nile.ch
    if (hostname.endsWith('nile.ch')) {
      return '.nile.ch';
    }

    // For myshopify.com domains, cookies can't be shared across stores
    // Return empty string to use current domain only
    return '';
  }

  /**
   * Set the preferences cookie
   * @param {Object} prefs - { country, language, store, ts }
   */
  function setNilePrefs(prefs) {
    const data = {
      country: prefs.country,
      language: prefs.language,
      store: prefs.store,
      ts: Date.now()
    };

    const expires = new Date();
    expires.setTime(expires.getTime() + (COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000));

    const cookieDomain = getCookieDomain();
    let cookieString = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}`;
    cookieString += `; expires=${expires.toUTCString()}`;
    cookieString += '; path=/';

    if (cookieDomain) {
      cookieString += `; domain=${cookieDomain}`;
    }

    cookieString += '; SameSite=Lax';

    // Only add Secure flag on HTTPS
    if (window.location.protocol === 'https:') {
      cookieString += '; Secure';
    }

    document.cookie = cookieString;
  }

  /**
   * Get the preferences from cookie ONLY (no localStorage fallback)
   * @returns {Object|null} - { country, language, store, ts } or null if not found
   */
  function getNilePrefs() {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === COOKIE_NAME && value) {
          return JSON.parse(decodeURIComponent(value));
        }
      }
    } catch (e) {
      console.warn('Error reading nile_prefs cookie:', e);
    }

    return null;
  }

  /**
   * Clear the preferences cookie
   */
  function clearNilePrefs() {
    const cookieDomain = getCookieDomain();
    let cookieString = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    if (cookieDomain) {
      cookieString += `; domain=${cookieDomain}`;
    }

    document.cookie = cookieString;

    // Also clear without domain (for myshopify.com environments)
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  }

  // ============================================
  // STORE DETECTION
  // ============================================

  /**
   * Determine which store the user is currently on
   * @returns {string} - 'chf', 'eur', 'gbp', or 'unknown'
   */
  function getCurrentStore() {
    const hostname = window.location.hostname;

    for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
      if (config.productionDomains.includes(hostname) || config.testDomains.includes(hostname)) {
        return storeId;
      }
    }

    // Fallback: check window.shopDomain if available
    if (window.shopDomain) {
      for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
        if (config.testDomains.includes(window.shopDomain)) {
          return storeId;
        }
      }
    }

    return 'unknown';
  }

  /**
   * Determine which store a country belongs to
   * @param {string} countryCode - ISO country code (e.g., 'DE', 'CH')
   * @returns {string} - 'chf', 'eur', 'gbp', or 'unknown'
   */
  function getStoreForCountry(countryCode) {
    if (!countryCode) return 'unknown';

    const code = countryCode.toUpperCase();

    for (const [storeId, config] of Object.entries(STORE_CONFIG)) {
      if (config.countries.includes(code)) {
        return storeId;
      }
    }

    return 'unknown';
  }

  /**
   * Check if we're on a test/development environment
   * @returns {boolean}
   */
  function isTestEnvironment() {
    const hostname = window.location.hostname;
    return hostname.includes('myshopify.com') || hostname.includes('localhost');
  }

  /**
   * Get the URL for a specific store
   * @param {string} storeId - 'chf', 'eur', or 'gbp'
   * @returns {string|null} - The store URL or null if not found
   */
  function getStoreUrl(storeId) {
    const config = STORE_CONFIG[storeId];
    if (!config) return null;

    const isTest = isTestEnvironment();

    if (isTest) {
      // For test environments, use themeSettings URLs if available
      if (window.themeSettings) {
        if (storeId === 'chf') {
          return window.themeSettings.test_ch_store_url || window.themeSettings.ch_store_url;
        } else if (storeId === 'eur') {
          return window.themeSettings.test_eu_store_url || window.themeSettings.eu_store_url;
        } else if (storeId === 'gbp') {
          return window.themeSettings.test_gb_store_url || window.themeSettings.gb_store_url;
        }
      }
      // Fallback to test domains
      return config.testDomains[0] ? `https://${config.testDomains[0]}` : null;
    } else {
      // Production
      if (window.themeSettings) {
        if (storeId === 'chf') {
          return window.themeSettings.ch_store_url || `https://${config.productionDomains[0]}`;
        } else if (storeId === 'eur') {
          return window.themeSettings.eu_store_url || `https://${config.productionDomains[0]}`;
        } else if (storeId === 'gbp') {
          return window.themeSettings.gb_store_url || `https://${config.productionDomains[0]}`;
        }
      }
      return config.productionDomains[0] ? `https://${config.productionDomains[0]}` : null;
    }
  }

  // ============================================
  // LANGUAGE DETECTION
  // ============================================

  /**
   * Get the browser's preferred language (simplified to supported languages)
   * @returns {string} - 'de', 'en', 'fr', 'it', or 'de' as fallback
   */
  function getBrowserLanguage() {
    const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];

    for (const lang of browserLanguages) {
      const baseLang = lang.split('-')[0].toLowerCase();
      if (SUPPORTED_LANGUAGES.includes(baseLang)) {
        return baseLang;
      }
    }

    return 'de'; // Default fallback
  }

  /**
   * Get the default language for a country, considering browser preference
   * @param {string} countryCode - ISO country code
   * @returns {string} - Language code
   */
  function getDefaultLanguage(countryCode) {
    const browserLang = getBrowserLanguage();

    // Browser language takes priority if it's supported
    if (SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }

    // Fall back to country default
    return COUNTRY_DEFAULT_LANGUAGE[countryCode] || COUNTRY_DEFAULT_LANGUAGE.default;
  }

  /**
   * Get the current language from URL path
   * @returns {string} - 'de', 'en', 'fr', 'it'
   */
  function getCurrentLanguageFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/^\/(de|en|fr|it)(\/|$)/);
    // No prefix means the store's own primary language, which is 'en' on GBP.
    return match ? match[1] : getPrimaryLanguage(getCurrentStore());
  }

  /**
   * Get the language a store serves at its root
   */
  function getPrimaryLanguage(storeId) {
    const config = STORE_CONFIG[storeId];
    return (config && config.primaryLanguage) || DEFAULT_PRIMARY_LANGUAGE;
  }

  /**
   * Build a path for a target store, prefixing the language only when it isn't
   * that store's primary one. This is what preserves language across stores.
   */
  function buildLocalePath(storeId, language, path) {
    let cleanPath = path === '/' ? '' : (path || '');
    if (cleanPath && !cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;

    if (!language || language === getPrimaryLanguage(storeId)) {
      return cleanPath;
    }

    return `/${language}${cleanPath}`;
  }

  /**
   * Strip any supported language prefix from a path
   */
  function stripLanguagePrefix(path) {
    const stripped = (path || '').replace(/^\/(de|en|fr|it)(?=\/|$)/, '');
    return stripped === '' ? '/' : stripped;
  }

  // ============================================
  // REDIRECT LOGIC
  // ============================================

  /**
   * Build the redirect URL for a different store
   * @param {string} targetStoreId - The store to redirect to
   * @param {string} language - The language to use
   * @param {string} countryCode - The country code to pass
   * @returns {string|null} - The full redirect URL or null
   */
  function buildRedirectUrl(targetStoreId, language, countryCode) {
    const targetBaseUrl = getStoreUrl(targetStoreId);
    if (!targetBaseUrl) return null;

    // Get current path without language prefix
    const currentPath = stripLanguagePrefix(window.location.pathname);

    // Prefix only when the language isn't the target store's primary one
    const newPath = buildLocalePath(targetStoreId, language, currentPath);

    // Construct the URL
    let targetUrl = targetBaseUrl.replace(/\/$/, '') + newPath;

    // Add query params
    const url = new URL(targetUrl);
    url.searchParams.set('redirected', 'true');
    url.searchParams.set('country', countryCode);

    // Preserve other query params
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (key !== 'redirected' && key !== 'country' && key !== 'modal_language' && key !== 'modal_country') {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }

  /**
   * Perform redirect to the correct store
   * @param {string} targetStoreId - The store to redirect to
   * @param {string} language - The language to use
   * @param {string} countryCode - The country code
   */
  function redirectToStore(targetStoreId, language, countryCode) {
    const redirectUrl = buildRedirectUrl(targetStoreId, language, countryCode);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }

  // ============================================
  // COUNTRY DETECTION
  // ============================================

  /**
   * Get detected country from Shopify (passed via theme.liquid)
   * @returns {string|null} - ISO country code or null
   */
  function getShopifyDetectedCountry() {
    return window.nileDetectedCountry || null;
  }

  /**
   * Fetch country from ipinfo.io as fallback
   * @returns {Promise<string|null>} - ISO country code or null
   */
  async function fetchCountryFromIpInfo() {
    try {
      const response = await fetch('https://ipinfo.io/json', { timeout: 5000 });
      const data = await response.json();
      return data.country || null;
    } catch (e) {
      console.warn('Could not fetch country from ipinfo.io:', e);
      return null;
    }
  }

  /**
   * Detect the user's country using available methods
   * @returns {Promise<string|null>} - ISO country code or null
   */
  async function detectCountry() {
    // Try Shopify's detection first (most reliable, no API call)
    const shopifyCountry = getShopifyDetectedCountry();
    if (shopifyCountry) {
      return shopifyCountry;
    }

    // Fallback to ipinfo.io
    return await fetchCountryFromIpInfo();
  }

  // ============================================
  // MAIN INITIALIZATION
  // ============================================

  /**
   * Check if user should be redirected based on saved preferences
   * This runs early on page load
   */
  function checkAndRedirect() {
    const prefs = getNilePrefs();

    if (!prefs || !prefs.country || !prefs.store) {
      // No saved preferences, let modal handle first-time visitors
      return false;
    }

    const currentStore = getCurrentStore();

    // If already on correct store, no redirect needed
    if (currentStore === prefs.store || currentStore === 'unknown') {
      return false;
    }

    // User has preferences and is on wrong store - redirect automatically
    console.log(`NILE Redirect: Redirecting from ${currentStore} to ${prefs.store} for country ${prefs.country}`);
    redirectToStore(prefs.store, prefs.language, prefs.country);
    return true;
  }

  /**
   * Handle first-time visitor detection
   * Called when no cookie exists
   * @returns {Promise<Object>} - { country, language, store, needsModal }
   */
  async function handleFirstTimeVisitor() {
    const detectedCountry = await detectCountry();

    if (!detectedCountry) {
      return { country: null, language: 'de', store: 'unknown', needsModal: true };
    }

    const targetStore = getStoreForCountry(detectedCountry);
    const currentStore = getCurrentStore();
    const language = getDefaultLanguage(detectedCountry);

    const result = {
      country: detectedCountry,
      language: language,
      store: targetStore,
      needsModal: currentStore !== targetStore && targetStore !== 'unknown'
    };

    // If on correct store, save preferences silently (no modal needed)
    if (!result.needsModal && targetStore !== 'unknown') {
      setNilePrefs({
        country: detectedCountry,
        language: language,
        store: targetStore
      });
    }

    return result;
  }

  // ============================================
  // PUBLIC API
  // ============================================

  // Expose functions globally for use by other scripts
  window.NileCountryRedirect = {
    // Cookie management
    setNilePrefs: setNilePrefs,
    getNilePrefs: getNilePrefs,
    clearNilePrefs: clearNilePrefs,

    // Store detection
    getCurrentStore: getCurrentStore,
    getStoreForCountry: getStoreForCountry,
    getStoreUrl: getStoreUrl,
    isTestEnvironment: isTestEnvironment,

    // Language helpers
    getBrowserLanguage: getBrowserLanguage,
    getDefaultLanguage: getDefaultLanguage,
    getCurrentLanguageFromUrl: getCurrentLanguageFromUrl,
    getPrimaryLanguage: getPrimaryLanguage,
    buildLocalePath: buildLocalePath,
    stripLanguagePrefix: stripLanguagePrefix,
    SUPPORTED_LANGUAGES: SUPPORTED_LANGUAGES,

    // Country detection
    detectCountry: detectCountry,
    getShopifyDetectedCountry: getShopifyDetectedCountry,

    // Redirect functions
    buildRedirectUrl: buildRedirectUrl,
    redirectToStore: redirectToStore,

    // First-time visitor handling
    handleFirstTimeVisitor: handleFirstTimeVisitor,

    // Configuration
    STORE_CONFIG: STORE_CONFIG,
    COUNTRY_DEFAULT_LANGUAGE: COUNTRY_DEFAULT_LANGUAGE
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  // Run redirect check immediately (before DOM is ready)
  // This ensures fast redirects for returning users.
  // Skip in the theme editor (Shopify.designMode is true only there): redirecting
  // the editor iframe or showing the country modal makes content editing impossible.
  const inThemeEditor = !!(window.Shopify && window.Shopify.designMode);
  const wasRedirected = inThemeEditor ? false : checkAndRedirect();

  // Store the redirect state for the modal script to check
  window.nileRedirectInProgress = wasRedirected;

})();
